import React, { type ReactElement, type ReactNode } from 'react';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import { LayerIndex } from './toPortInfo.tsx';

type StrictChild =
	| StrictChild[]
	| ReactElement<LayerItemProps[]>
	| ReactElement<LayerItemProps>
	| null
	| undefined
	| false;

type LayerGroupProps = {
	children: StrictChild[];
};

type LayerItemProps = {
	layer: LayerIndex;
	children: ReactNode;
};

/**
 * Container component for an array of LayerItems. This container sorts the child LayerItems using the value of their `layer` prop
 *
 * @example
 * // simple use case
 * <LayerGroup>
 *     <LayerItem layer={3}>Put on top</LayerItem>
 *     <LayerItem layer={1}>Below</LayerItem>
 *     <LayerItem layer={2}>Middle level</LayerItem>
 * </LayerGroup>
 *
 * @example
 * // Arrays, and conditions with falsey returns are also allowed
 * <LayerGroup>
 *    {items.map(x => <LayerItem layer={1}>{x}</LayerItem>)}
 *    {isFoo ? <LayerItem layer={2}>bar</LayerItem> : null}
 * </LayerGroup>
 * */
export const LayerGroup = ({ children }: LayerGroupProps): React.JSX.Element => (
	<>
		{sortBy(
			children.flatMap((child) => (Array.isArray(child) ? child : [child])),
			(child) => Number(get(child, 'props.layer', LayerIndex.Default)),
		)}
	</>
);

/**
 * Wraps SVG components that will be sorted by the direct parent <LayerGroup> using the `layer` prop
 *
 * Note that if the layer depends on internal code of a LayerItem's descendant, you will have to pass it up
 * to where the enclosing <LayerItem> and handle this manually via state
 *
 * Sublayering or nested LayerGroups are not supported
 *
 * @example
 *     <LayerItem layer={3}><Foo /></LayerItem>
 *     <LayerItem layer={LayerIndex.Proposal}><Foo /></LayerItem>
 *
 * */
export const LayerItem = ({ children }: LayerItemProps): React.JSX.Element => <>{children}</>;
